import type { FlexProps } from '@chakra-ui/react';
import { Badge, Flex, HStack, Spacer, Tabs } from '@chakra-ui/react';

import React, { useCallback, useMemo } from 'react';
import { BackButton } from '@/components/ui/back-icon-button';

export interface TabConfig {
  value: string;
  label: string;
  icon?: React.ElementType;
  badgeCount?: number;
  badgeAriaLabel?: string;
}

interface TopNavProps extends FlexProps {
  onBack?: () => void;
  backButtonLabel?: string;
  rightSection?: React.ReactNode | null;
  middleSection?: React.ReactNode;
  leftSection?: React.ReactNode;
  title?: string;
  isSticky?: boolean;
  position?: 'fixed' | 'sticky' | 'relative';
  tabs?: TabConfig[];
  activeTab?: string;
  onTabChange?: (value: string) => void;
}

const formatBadgeCount = (count: number | undefined): string | null => {
  if (typeof count !== 'number' || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
};

const TopNav = React.memo<TopNavProps>(
  ({
    onBack,
    backButtonLabel,
    rightSection,
    middleSection,
    leftSection,
    title: propTitle,
    isSticky = false,
    position: positionProp,
    tabs,
    activeTab,
    onTabChange,
    ...props
  }) => {
    // Use useMemo for position calculation with proper typing
    const position = useMemo<'fixed' | 'sticky' | 'relative'>(() => {
      if (positionProp) return positionProp;
      if (isSticky || tabs) return 'sticky';
      return 'relative';
    }, [positionProp, isSticky, tabs]);

    const isFixed = position === 'fixed';
    const isPositionSticky = position === 'sticky';
    const isPinned = isFixed || isPositionSticky;

    const handleTabChange = useCallback(
      (value: string) => {
        onTabChange?.(value);
      },
      [onTabChange]
    );

    const renderTabsSection = useCallback(() => {
      if (tabs && tabs.length > 0) {
        return (
          <Flex h="full" alignItems="flex-end">
            <Tabs.Root
              value={activeTab}
              onValueChange={(details) => handleTabChange(details.value)}
              variant="line"
              colorPalette="primary"
              borderBottomColor="red.300"
              size="md"
            >
              <Tabs.List
                h="auto"
                borderBottom="none"
                gapX={5}
                role="tablist"
                aria-label="Navigation tabs"
              >
                {tabs.map((tab) => {
                  const badgeLabel = formatBadgeCount(tab.badgeCount);
                  const ariaLabel = tab.badgeAriaLabel
                    ? `${tab.label}, ${tab.badgeAriaLabel}`
                    : tab.label;

                  return (
                    <Tabs.Trigger
                      key={tab.value}
                      value={tab.value}
                      fontSize="15px"
                      fontWeight="medium"
                      alignItems="center"
                      pb={3}
                      pt={3}
                      px={0}
                      transition="all 0.2s"
                      mb={0}
                      aria-label={ariaLabel}
                      aria-selected={activeTab === tab.value}
                      tabIndex={activeTab === tab.value ? 0 : -1}
                      letterSpacing="wide"
                    >
                      <HStack gap={1.5}>
                        {tab.icon &&
                          React.createElement(tab.icon, {
                            width: 20,
                            height: 20,
                            'aria-hidden': true,
                          })}
                        {tab.label}
                        {badgeLabel ? (
                          <Badge
                            colorPalette="gray"
                            variant="solid"
                            borderRadius="full"
                            minW={4}
                            h={4}
                            px={1}
                            justifyContent="center"
                            fontSize="2xs"
                            lineHeight="1"
                          >
                            {badgeLabel}
                          </Badge>
                        ) : null}
                      </HStack>
                    </Tabs.Trigger>
                  );
                })}
              </Tabs.List>
            </Tabs.Root>
          </Flex>
        );
      }
      return null;
    }, [tabs, activeTab, handleTabChange]);

    const renderLeftSection = useCallback(() => {
      if (leftSection) return leftSection;
      const navigation = onBack ? (
        <BackButton
          onClick={onBack}
          label={backButtonLabel}
          aria-label={backButtonLabel ? `Go back to ${backButtonLabel}` : 'Go back'}
        />
      ) : null;

      const tabsElement = renderTabsSection();

      return (
        <Flex align="stretch" gap={4} minW="0" h="full">
          {navigation && <Flex align="center">{navigation}</Flex>}
          {tabsElement}
        </Flex>
      );
    }, [leftSection, onBack, backButtonLabel, renderTabsSection]);

    const renderMiddleSection = useCallback(() => {
      return middleSection ? (
        <Flex flex={1} justify="center" mx={4}>
          {middleSection}
        </Flex>
      ) : (
        <Spacer />
      );
    }, [middleSection]);

    const renderRightSection = useCallback(() => {
      return rightSection !== undefined ? rightSection : null;
    }, [rightSection]);

    return (
      <Flex
        as="header"
        justify="space-between"
        align="center"
        w="100%"
        h="60px"
        mb={0}
        px={6}
        position={position}
        top={isPinned ? 0 : undefined}
        zIndex={isPinned ? (isFixed ? 'docked' : 'sticky') : undefined}
        bg={isPinned ? 'white/95' : 'white'}
        backdropFilter={isPinned ? 'blur(10px)' : undefined}
        left={0}
        right={0}
        {...props}
      >
        {renderLeftSection()}
        {renderMiddleSection()}
        {renderRightSection()}
      </Flex>
    );
  }
);

TopNav.displayName = 'TopNav';

export default TopNav;
